import type { MouseEvent } from 'react';

import { getTextWidth, truncateText } from '../../utilities';
import styles from './sankey-chart.module.css';
import type { SankeyNodeValidated } from './types';

const LABEL_FONT_SIZE = 12;
export const VALUE_FONT_SIZE = 11;
export const LABEL_PADDING = 8;
export const INTERIOR_LABEL_GAP = 18;
const INTERIOR_LABEL_BASELINE_GAP = (INTERIOR_LABEL_GAP * 2) / 3;
const HIT_PADDING = 2;

type NodeLabelHoverProps = {
  onMouseEnter: () => void;
  onMouseMove: (event: MouseEvent<SVGElement>) => void;
  onMouseLeave: () => void;
};

type NodeLabelProps = {
  node: SankeyNodeValidated;
  centerY: number;
  isFirstColumn: boolean;
  isLastColumn: boolean;
  sideLabelMaxWidth: number;
  interiorLabelMaxWidth: number;
  label: string;
  value?: string;
  fontSize?: number;
  /** Hover target for the node tooltip: an invisible rect sized to the
   * rendered text (the text keeps pointer-events: none). */
  hoverProps?: NodeLabelHoverProps;
};

/** Outer-column labels sit in the side margins; interior labels center above the bar. */
export const NodeLabel = ({
  node,
  centerY,
  isFirstColumn,
  isLastColumn,
  sideLabelMaxWidth,
  interiorLabelMaxWidth,
  label,
  value,
  fontSize = LABEL_FONT_SIZE,
  hoverProps,
}: NodeLabelProps) => {
  if (isFirstColumn || isLastColumn) {
    const x = isFirstColumn ? node.x0 - LABEL_PADDING : node.x1 + LABEL_PADDING;
    const textAnchor = isFirstColumn ? 'end' : 'start';
    const testId = isFirstColumn ? 'sankey-source-node' : 'sankey-target-node';
    const truncated = truncateText({
      text: label,
      maxWidth: sideLabelMaxWidth,
      fontSize,
    });
    const hitWidth =
      Math.max(
        getTextWidth({ text: truncated, fontSize }) ?? 0,
        value === undefined
          ? 0
          : (getTextWidth({ text: value, fontSize: VALUE_FONT_SIZE }) ?? 0)
      ) +
      HIT_PADDING * 2;
    const hitHeight =
      fontSize +
      HIT_PADDING * 2 +
      (value === undefined ? 0 : VALUE_FONT_SIZE * 1.2);
    return (
      <>
        {hoverProps !== undefined && (
          <rect
            x={isFirstColumn ? x - hitWidth : x}
            y={centerY - fontSize / 2 - HIT_PADDING}
            width={hitWidth}
            height={hitHeight}
            fill="transparent"
            className={styles.node}
            {...hoverProps}
          />
        )}
        <text
          x={x}
          y={centerY}
          textAnchor={textAnchor}
          dominantBaseline="middle"
          fontSize={fontSize}
          className={styles.label}
          data-testid={testId}
        >
          <title>{label}</title>
          {truncated}
          {value !== undefined && (
            <tspan
              x={x}
              dy="1.2em"
              fontSize={VALUE_FONT_SIZE}
              className={styles.value}
            >
              {value}
            </tspan>
          )}
        </text>
      </>
    );
  }

  const centerX = (node.x0 + node.x1) / 2;
  const truncated = truncateText({
    text: label,
    maxWidth: interiorLabelMaxWidth,
    fontSize,
  });
  const renderedText =
    value === undefined ? truncated : `${truncated} (${value})`;
  const hitWidth =
    (getTextWidth({ text: renderedText, fontSize }) ?? 0) + HIT_PADDING * 2;
  return (
    <>
      {hoverProps !== undefined && (
        <rect
          x={centerX - hitWidth / 2}
          y={node.y0 - INTERIOR_LABEL_BASELINE_GAP - fontSize}
          width={hitWidth}
          height={fontSize + HIT_PADDING * 3}
          fill="transparent"
          className={styles.node}
          {...hoverProps}
        />
      )}
      <text
        x={centerX}
        y={node.y0 - INTERIOR_LABEL_BASELINE_GAP}
        textAnchor="middle"
        fontSize={fontSize}
        className={styles.label}
        data-testid="sankey-center-node"
      >
        <title>{label}</title>
        {renderedText}
      </text>
    </>
  );
};
